import i18n, { type ResourceKey } from "i18next";
import { initReactI18next } from "react-i18next";

import de from "./de.json";
import en from "./en.json";
import { localeBackend } from "./localeBackend";

const savedLang = localStorage.getItem("alta_demo_lang")
  ?? localStorage.getItem("alta_lang")
  ?? "de";

// Several startup effects request the same global dictionary. Share the
// request and the applied bundle briefly; never persist remote dictionaries.
const remoteOverrides = new Map<string, { promise: Promise<void>; expires: number }>();

i18n.on("languageChanged", (lang: string) => {
  void loadRemoteOverrides(lang);
});

i18n.use(localeBackend).use(initReactI18next).init({
  resources: {
    de: { translation: de },
    en: { translation: en },
  },
  partialBundledLanguages: true,
  // Locale assets and existing overrides use lower-case keys (en-gb).
  lowerCaseLng: true,
  lng: savedLang,
  fallbackLng: {
    "en-gb": ["en", "de"],
    pl:      ["en", "de"],
    it:      ["en", "de"],
    nl:      ["en", "de"],
    default: ["de"],
  },
  interpolation: { escapeValue: false },
});

/**
 * Load remote translation overrides for a given language from the backend.
 * Overrides are stored by admins via the Übersetzungen page (Task #997).
 * Auto-loaded on init and on every language change.
 */
export function loadRemoteOverrides(lang: string): Promise<void> {
  const cached = remoteOverrides.get(lang);
  if (cached && cached.expires > Date.now()) return cached.promise;
  const promise = (async () => {
    try {
      const res = await fetch(`/api/i18n/${lang}`);
      if (!res.ok) throw new Error("Remote translations unavailable");
      const data = await res.json();
      if (data && typeof data === "object" && Object.keys(data).length > 0) {
        i18n.addResourceBundle(lang, "translation", data, true, true);
      }
    } catch {
      // Do not retain failures: a subsequent language change can recover.
      remoteOverrides.delete(lang);
    }
  })();
  remoteOverrides.set(lang, { promise, expires: Date.now() + 60_000 });
  return promise;
}

/**
 * Augment only fully loaded bundles. Creating an optional language's partial
 * bundle early would make i18next skip its real language-file download.
 */
export function registerLocalTranslations(resources: Record<string, ResourceKey>): void {
  const applied = new Set<string>();
  const apply = () => {
    for (const [language, values] of Object.entries(resources)) {
      if (!applied.has(language) && i18n.hasResourceBundle(language, "translation")) {
        i18n.addResourceBundle(language, "translation", values, true, true);
        applied.add(language);
      }
    }
  };
  apply();
  i18n.on("languageChanged", apply);
}

export default i18n;
